<template>
  <ds-dialog id="investigation-options" title="Opties" size="lg" @close="$emit('close')">
    <div class="investigation-options-dialog">
      <div class="investigation-options-dialog__bar">
        <input
          v-model="query"
          type="search"
          class="ds-field"
          placeholder="Zoek op code, omschrijving of tag…"
          data-qa="investigation-options-search"
          aria-label="Zoek in opties"
        />
        <span class="investigation-options-dialog__count">
          {{ visibleOptions.length }} van {{ options.length }}
        </span>
      </div>

      <div class="ds-table investigation-options-dialog__table">
        <div class="ds-table__scroll">
          <table>
            <thead>
              <tr>
                <th
                  v-for="column in columns"
                  :key="column.label"
                  class="ds-table__header-cell"
                  :style="{ width: column.width }"
                >
                  <div class="ds-table__header-inner">
                    <span class="ds-table__label ds-table__label--static">{{ column.label }}</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="option in visibleOptions" :key="option.key">
                <td class="ds-table__code">{{ option.code }}</td>
                <td>{{ option.description || '—' }}</td>
                <td>
                  <span class="ds-chip" :class="option.classificationTone">
                    {{ option.classification || '—' }}
                  </span>
                </td>
                <td>
                  <span v-if="!option.tags.length" class="investigation-options-dialog__none">
                    —
                  </span>
                  <div
                    v-for="(tag, index) in option.tags"
                    :key="index"
                    class="investigation-options-dialog__tags"
                  >
                    <span v-if="tag.family" class="investigation-options-dialog__tag">
                      <em>salesFamily</em>{{ tag.family }}
                    </span>
                    <span
                      v-if="tag.label"
                      class="investigation-options-dialog__tag investigation-options-dialog__tag--label"
                    >
                      <em>label</em>{{ tag.label }}
                    </span>
                  </div>
                </td>
              </tr>
              <tr v-if="!visibleOptions.length">
                <td colspan="4" class="ds-table__empty">Geen opties gevonden.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </ds-dialog>
</template>

<script>
import DsDialog from '@/components/ds/molecules/dsDialog/DsDialog.vue'

// Every option the GET_STANDARD_OPTIONS call returned, unfiltered — the list
// behind the "N gekozen · M standaard" line on the configuration card.

const CLASSIFICATION_TONES = {
  STANDARD_EQUIPMENT: 'ds-chip--neutral',
  OPTIONAL_EQUIPMENT: 'ds-chip--info',
  LOCAL_EQUIPMENT: 'ds-chip--brand',
}

const COLUMNS = [
  { label: 'Code', width: '26%' },
  { label: 'Omschrijving', width: '32%' },
  { label: 'Classification', width: '17%' },
  { label: 'Tags', width: '25%' },
]

// tag_groups[].tags[] — per group the salesFamily and label descriptions.
function readTags(option) {
  return (option.tag_groups || [])
    .map((group) => {
      const tags = group.tags || []
      const describe = (type) => tags.find((tag) => tag.tag_type === type)?.description || null
      return { family: describe('salesFamily'), label: describe('label') }
    })
    .filter((tag) => tag.family || tag.label)
}

export default {
  name: 'InvestigationOptionsDialog',
  components: { DsDialog },
  props: {
    options: {
      type: Array,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    return { query: '', columns: COLUMNS }
  },
  computed: {
    // The tags are read once per option here rather than per render: they are
    // both the search haystack and the rendered cell.
    rows() {
      return this.options.map((option) => ({
        key: option.id || option.code,
        code: option.code,
        description: option.description,
        classification: option.classification,
        classificationTone: CLASSIFICATION_TONES[option.classification] || 'ds-chip--neutral',
        tags: readTags(option),
      }))
    },
    visibleOptions() {
      const term = this.query.trim().toLowerCase()
      if (!term) return this.rows

      return this.rows.filter((row) => {
        const fields = [row.code, row.classification, row.description]
        if (fields.some((field) => (field || '').toLowerCase().includes(term))) return true
        return row.tags.some((tag) =>
          [tag.family, tag.label].some((value) => (value || '').toLowerCase().includes(term))
        )
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// The option list is four columns of codes and tags, so it needs more room than
// the widest shared dialog size. The panel is rendered by DsDialog, hence the
// :deep() off this component's root.
:deep(.ds-dialog__panel) {
  max-width: min(1600px, 94vw);
}

.investigation-options-dialog {
  &__bar {
    display: flex;
    align-items: center;
    gap: var(--ds-space-3);
    flex-wrap: wrap;
    margin-bottom: var(--ds-space-4);

    .ds-field {
      flex: 1;
      min-width: 220px;
      width: auto;
    }
  }

  &__count {
    font-size: var(--ds-font-size-sm);
    color: var(--ds-color-text-subtle);
  }

  &__table :deep(.ds-table__scroll) {
    max-height: min(60vh, 700px);
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;

    + .investigation-options-dialog__tags {
      margin-top: 6px;
    }
  }

  &__tag {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
    padding: 3px 9px;
    border-radius: var(--ds-radius-full);
    background: var(--ds-color-border-subtle);
    color: var(--ds-color-text);
    font-size: 12.5px;
    line-height: 1.35;

    em {
      font-style: normal;
      font-size: 9.5px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--ds-color-text-subtle);
    }

    &--label {
      background: var(--ds-color-accent-subtle);
      color: var(--ds-color-brand);

      em {
        color: currentcolor;
        opacity: 0.6;
      }
    }
  }

  &__none {
    color: var(--ds-color-text-subtle);
  }
}
</style>
